'use client';

import PropTypes from 'prop-types';
import {useSnackbar} from "notistack";
import React, {useState, useCallback} from "react";

import Stack from '@mui/material/Stack';
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import {paths} from "src/routes/paths";
import {useRouter} from "src/routes/hooks";

import {useAuthContext} from "src/auth/hooks";
import {voteComment} from "src/services/comment/vote-api";
// ----------------------------------------------------------------------

export default function CommentVote({recipeData, comment, ...others}) {
  const router = useRouter();
  const {enqueueSnackbar} = useSnackbar();
  const {user} = useAuthContext();

  const isUserInUpVotes = user?.pimcoreUserId && comment.upVotes?.filter((vote) => vote.id === user.pimcoreUserId).length > 0;
  const isUserInDownVotes = user?.pimcoreUserId && comment.downVotes?.filter((vote) => vote.id === user.pimcoreUserId).length > 0;

  const [hasUserVotedUp, setHasUserVotedUp] = useState(isUserInUpVotes);
  const [hasUserVotedDown, setHasUserVotedDown] = useState(isUserInDownVotes);

  const calculatedVoteCount = (comment.upVoteCount ?? 0)
    - (comment.downVoteCount ?? 0)
    + (hasUserVotedUp && !isUserInUpVotes ? 1 : 0)
    - (hasUserVotedDown && !isUserInDownVotes ? 1 : 0)
    + (!hasUserVotedDown && isUserInDownVotes ? 1 : 0)
    - (!hasUserVotedUp && isUserInUpVotes ? 1 : 0);

  const handleUpVote = useCallback(async () => {
    if (!user || user.isLoggedIn === false) {
      enqueueSnackbar('Musisz być zalogowany, aby oceniać komentarze.', {variant: 'error'});
      router.push(paths.auth.token.loginWithReturnTo(paths.recipe.details(recipeData.pimcoreRecipeId)));
      return;
    }

    if (hasUserVotedUp) {
      voteComment({commentId: comment.pimcoreCommentId, type: 'cancel'});
      setHasUserVotedUp(false);
      setHasUserVotedDown(false);
      enqueueSnackbar('Cofnięto Twój głos!', {variant: 'success'});
    } else {
      voteComment({commentId: comment.pimcoreCommentId, type: 'up'});
      setHasUserVotedUp(true);
      setHasUserVotedDown(false);
      enqueueSnackbar('Dziękujemy za oddany głos!', {variant: 'success'});
    }

  }, [recipeData.pimcoreRecipeId, router, user, hasUserVotedUp, setHasUserVotedUp, comment.pimcoreCommentId, enqueueSnackbar]);

  const handleDownVote = useCallback(async () => {
    if (!user || user.isLoggedIn === false) {
      enqueueSnackbar('Musisz być zalogowany, aby oceniać komentarze.', {variant: 'error'});
      router.push(paths.auth.token.loginWithReturnTo(paths.recipe.details(recipeData.pimcoreRecipeId)));
      return;
    }

    if (hasUserVotedDown) {
      voteComment({commentId: comment.pimcoreCommentId, type: 'cancel'});
      setHasUserVotedDown(false);
      setHasUserVotedUp(false);
      enqueueSnackbar('Cofnięto Twój głos!', {variant: 'success'});
    } else {
      voteComment({commentId: comment.pimcoreCommentId, type: 'down'});
      setHasUserVotedDown(true);
      setHasUserVotedUp(false);
      enqueueSnackbar('Dziękujemy za oddany głos!', {variant: 'success'});
    }
  }, [recipeData.pimcoreRecipeId, router, user, setHasUserVotedDown, hasUserVotedDown, comment.pimcoreCommentId, enqueueSnackbar]);

  return (
    <Stack direction="column" spacing={0.2} sx={{width: {md: '120px'}, flexShrink: {md: 0}, alignItems: 'center'}}>
      <Button sx={{width: {xs: '25px', md: '64px'}, minWidth: 'auto', fontSize: '20px'}} size="small" color="primary"
              variant={hasUserVotedUp ? 'contained' : 'soft'} onClick={handleUpVote}>
        +
      </Button>
      <Typography variant="h6">{calculatedVoteCount}</Typography>
      <Button sx={{width: {xs: '25px', md: '64px'}, minWidth: 'auto', fontSize: '20px'}} size="small" color="error"
              variant={hasUserVotedDown ? 'contained' : 'soft'} onClick={handleDownVote}>
        -
      </Button>
    </Stack>
  );
}

CommentVote.propTypes = {
  recipeData: PropTypes.object,
  comment: PropTypes.object
};
